import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import { Button, Form } from "react-bootstrap";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import CustomInput from "../lib/CustomInput";
import { createWomenFanCard } from "../../../api/womenFanCard/womenFanCardApi";
import * as Yup from "yup";
import { Calendar } from "@hassanmojab/react-modern-calendar-datepicker";
import "@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css";
import moment from "jalali-moment";
import { KTIcon } from "../../../../_metronic/helpers";
import { addFile } from "../../../api/files/filesApi";

export enum Education {
  NONE = "none",
  HIGH_SCHOOL = "highSchool",
  BACHELOR = "bachelor",
  MASTER = "master",
  DOCTORATE = "doctorate",
}

const educationLabels: Record<Education, string> = {
  [Education.NONE]: "بدون تحصیلات",
  [Education.HIGH_SCHOOL]: "دیپلم",
  [Education.BACHELOR]: "کارشناسی",
  [Education.MASTER]: "کارشناسی ارشد",
  [Education.DOCTORATE]: "دکتری",
};

interface RequestBody {
  firstName: string;
  lastName: string;
  nationalCode: string;
  nationalNumber: string;
  dateOfBirth: string;
  education?: Education;
  postalCode: string;
  fullAddress: string;
  phoneNumber?: string;
  mobileNumber: string;
  fatherName: string;
  nationalCardImage: File;
  personalImage: File;
}

interface FormValues {
  firstName: string;
  lastName: string;
  nationalCode: string;
  nationalNumber: string;
  dateOfBirth: string;
  education: Education | "";
  postalCode: string;
  fullAddress: string;
  phoneNumber: string;
  mobileNumber: string;
  fatherName: string;
  nationalCardImage: File | null;
  personalImage: File | null;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("نام الزامی است"),
  lastName: Yup.string().required("نام خانوادگی الزامی است"),
  nationalCode: Yup.string()
    .required("کد ملی الزامی است")
    .matches(/^[0-9]{10}$/, "کد ملی باید 10 رقم باشد"),
  nationalNumber: Yup.string().required("شماره شناسنامه الزامی است"),
  dateOfBirth: Yup.string().required("تاریخ تولد الزامی است"),
  education: Yup.string(),
  postalCode: Yup.string()
    .required("کد پستی الزامی است")
    .matches(/^[0-9]{10}$/, "کد پستی باید 10 رقم باشد"),
  fullAddress: Yup.string().required("آدرس کامل الزامی است"),
  phoneNumber: Yup.string(),
  mobileNumber: Yup.string()
    .required("شماره موبایل الزامی است")
    .matches(/^09[0-9]{9}$/, "شماره موبایل باید با 09 شروع شود و 11 رقم باشد"),
  fatherName: Yup.string().required("نام پدر الزامی است"),
  nationalCardImage: Yup.mixed().required("تصویر کارت ملی الزامی است"),
  personalImage: Yup.mixed().required("تصویر پرسنلی الزامی است"),
});

function CreateFanCard() {
  const [selectedFiles, setSelectedFiles] = useState({
    nationalCardImage: null as File | null,
    personalImage: null as File | null,
  });
  const [datePickerFlag, setDatePickerFlag] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setDatePickerFlag(false);
      }
    };

    if (datePickerFlag) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [datePickerFlag]);

  const formik = useFormik<FormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      nationalCode: "",
      nationalNumber: "",
      dateOfBirth: "",
      education: "",
      postalCode: "",
      fullAddress: "",
      phoneNumber: "",
      mobileNumber: "",
      fatherName: "",
      nationalCardImage: null,
      personalImage: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!selectedFiles.nationalCardImage || !selectedFiles.personalImage) {
        toast.error("لطفا تصاویر مورد نیاز را آپلود کنید");
        return;
      }

      const formData = new FormData();
      formData.append('nationalCardImage', selectedFiles.nationalCardImage);
      formData.append('personalImage', selectedFiles.personalImage);
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('nationalCode', values.nationalCode);
      formData.append('nationalNumber', values.nationalNumber);
      formData.append('dateOfBirth', convertToGregorianDate(selectedDate));
      formData.append('postalCode', values.postalCode);
      formData.append('fullAddress', values.fullAddress);
      formData.append('mobileNumber', values.mobileNumber);
      formData.append('fatherName', values.fatherName);

      if (values.phoneNumber) {
        formData.append('phoneNumber', values.phoneNumber);
      }

      if (values.education) {
        formData.append('education', values.education as Education);
      }

      createFanCard.mutate(formData);
    },
  });

  const createFanCard = useMutation({
    mutationFn: async (formData: FormData) => {
      return await createWomenFanCard(formData);
    },
    onSuccess: () => {
      toast.success("فن کارت با موفقیت ایجاد شد");
      formik.resetForm();
      setSelectedFiles({
        nationalCardImage: null,
        personalImage: null,
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "خطا در ایجاد فن کارت");
    },
  });

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 0) { // Ensure file is not empty
        setSelectedFiles((prev) => ({
          ...prev,
          [fieldName]: file,
        }));
        formik.setFieldValue(fieldName, file);
      } else {
        toast.error("فایل انتخاب شده خالی است");
      }
    }
  };

  const handleDateChange = (date: any) => {
    if (date) {
      const formattedDate = `${date.year}/${date.month}/${date.day}`;
      formik.setFieldValue("dateOfBirth", formattedDate);
    }
  };

  const dateValue = (date: any) => {
    if (!date) return "";
    const month = date.month.toString().padStart(2, "0");
    const day = date.day.toString().padStart(2, "0");
    return `${date.year}/${month}/${day}`;
  };

  const convertToGregorianDate = (date: any) => {
    if (!date) return "";
    const jalaliDate = `${date.year}/${date.month}/${date.day}`;
    const momentDate = moment(jalaliDate, 'jYYYY/jMM/jDD');
    const gregorianDate = momentDate.format('YYYY-MM-DD');
    return gregorianDate;
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">صدور کارت هواداری</h3>
      </div>
      <Form onSubmit={formik.handleSubmit}>
        <div className="card-body">
          <div className="row mb-6">
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="نام"
                placeholder="نام"
                fieldName="firstName"
                required
              />
            </div>
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="نام خانوادگی"
                placeholder="نام خانوادگی"
                fieldName="lastName"
                required
              />
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="کد ملی"
                placeholder="کد ملی"
                fieldName="nationalCode"
                required
              />
            </div>
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="شماره شناسنامه"
                placeholder="شماره شناسنامه"
                fieldName="nationalNumber"
                required
              />
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-12 col-lg-6">
              <label htmlFor="" className="form-label fw-bold">
                تاریخ تولد
              </label>
              <div className="input-group mb-3 position-relative">
                <button
                  className="btn btn-primary"
                  type="button"
                  id="button-addon1"
                  onClick={() => {
                    setDatePickerFlag(!datePickerFlag);
                  }}
                >
                  انتخاب تاریخ
                </button>
                <input
                  type="text"
                  value={selectedDate ? dateValue(selectedDate) : ""}
                  className="form-control"
                  placeholder=""
                  aria-label="Example text with button addon"
                  aria-describedby="button-addon1"
                  readOnly
                />
                {datePickerFlag && (
                  <div ref={calendarRef}>
                    <Calendar
                      value={selectedDate}
                      onChange={(e) => {
                        if (e) {
                          setSelectedDate(e);
                          formik.setFieldValue("dateOfBirth", `${e.year}/${e.month}/${e.day}`);
                          setDatePickerFlag(false);
                        }
                      }}
                      locale="fa"
                      calendarClassName="responsive-calendar position-absolute calendarStyle"
                    />
                  </div>
                )}
              </div>
              {formik.errors.dateOfBirth && formik.touched.dateOfBirth && (
                <div className="text-danger mt-1">
                  {formik.errors.dateOfBirth}
                </div>
              )}
            </div>
            <div className="col-6">
              <Form.Group>
                <Form.Label>تحصیلات</Form.Label>
                <Form.Select
                  name="education"
                  value={formik.values.education}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="form-select"
                >
                  <option value="">انتخاب کنید</option>
                  {Object.entries(educationLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="کد پستی"
                placeholder="کد پستی"
                fieldName="postalCode"
                required
              />
            </div>
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="آدرس کامل"
                placeholder="آدرس کامل"
                fieldName="fullAddress"
                required
              />
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="شماره تلفن"
                placeholder="شماره تلفن"
                fieldName="phoneNumber"
              />
            </div>
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="شماره موبایل"
                placeholder="شماره موبایل"
                fieldName="mobileNumber"
                required
              />
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-6">
              <CustomInput
                formik={formik}
                label="نام پدر"
                placeholder="نام پدر"
                fieldName="fatherName"
                required
              />
            </div>
          </div>

          <div className="row mb-6">
            <div className="col-6">
              <div className="image-input image-input-outline" data-kt-image-input="true" style={{ cursor: 'pointer' }}>
                <div className="image-input-wrapper w-125px h-125px" style={{ backgroundImage: selectedFiles.nationalCardImage ? `url(${URL.createObjectURL(selectedFiles.nationalCardImage as File)})` : 'none' }}>
                  <input type="file" name="nationalCardImage" accept=".png, .jpg, .jpeg" onChange={(e) => handleFileChange(e, "nationalCardImage")} style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                </div>
                <label className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="change" data-bs-toggle="tooltip" title="تغییر تصویر">
                  <i className="bi bi-pencil-fill fs-7"></i>
                </label>
                <span className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="cancel" data-bs-toggle="tooltip" title="حذف تصویر">
                  <i className="bi bi-x fs-2"></i>
                </span>
                <span className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="remove" data-bs-toggle="tooltip" title="حذف تصویر">
                  <i className="bi bi-x fs-2"></i>
                </span>
              </div>
              <div className="form-text">تصویر کارت ملی</div>
              {formik.errors.nationalCardImage && formik.touched.nationalCardImage && (
                <div className="text-danger mt-1">
                  {typeof formik.errors.nationalCardImage === 'string' ? formik.errors.nationalCardImage : 'تصویر کارت ملی الزامی است'}
                </div>
              )}
            </div>
            <div className="col-6">
              <div className="image-input image-input-outline" data-kt-image-input="true" style={{ cursor: 'pointer' }}>
                <div className="image-input-wrapper w-125px h-125px" style={{ backgroundImage: selectedFiles.personalImage ? `url(${URL.createObjectURL(selectedFiles.personalImage as File)})` : 'none' }}>
                  <input type="file" name="personalImage" accept=".png, .jpg, .jpeg" onChange={(e) => handleFileChange(e, "personalImage")} style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                </div>
                <label className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="change" data-bs-toggle="tooltip" title="تغییر تصویر">
                  <i className="bi bi-pencil-fill fs-7"></i>
                </label>
                <span className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="cancel" data-bs-toggle="tooltip" title="حذف تصویر">
                  <i className="bi bi-x fs-2"></i>
                </span>
                <span className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow" data-kt-image-input-action="remove" data-bs-toggle="tooltip" title="حذف تصویر">
                  <i className="bi bi-x fs-2"></i>
                </span>
              </div>
              <div className="form-text">تصویر پرسنلی</div>
              {formik.errors.personalImage && formik.touched.personalImage && (
                <div className="text-danger mt-1">
                  {typeof formik.errors.personalImage === 'string' ? formik.errors.personalImage : 'تصویر پرسنلی الزامی است'}
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Button
                type="submit"
                variant="primary"
                disabled={createFanCard.isLoading}
              >
                {createFanCard.isLoading ? "در حال ایجاد..." : "ایجاد فن کارت"}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}

export default CreateFanCard;
